import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Fingerprint,
  Download,
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  Database,
  RefreshCw,
  Users,
  Clock
} from 'lucide-react';
import './BiometricoSyncModal.css';

const BiometricoSyncModal = ({ isOpen, onClose }) => {
  const [config, setConfig] = useState({
    nombreDispositivo: '',
    ip: '192.168.1.201',
    puerto: 4370,
    timeout: 10000,
    servidorDB: '',
    usuarioDB: '',
    passwordDB: '',
    baseDatos: '',
    tablaRegistros: 'registro',
    tablaHuellas: 'cat_biometricos_empleados',
    rutaXampp: 'C:\\xampp'
  });

  const [status, setStatus] = useState({
    conectadoDispositivo: false,
    conectadoDB: false
  });

  const [syncStatus, setSyncStatus] = useState({
    type: null, // 'huellas' | 'checadas'
    inProgress: false,
    progress: 0,
    total: 0,
    message: '',
    success: null,
    details: []
  });

  const [stats, setStats] = useState({
    huellasEnDB: 0,
    huellasEnDispositivo: 0,
    checadasPendientes: 0
  });

  const [timeSyncStatus, setTimeSyncStatus] = useState({
    inProgress: false,
    message: '',
    success: null
  });

  const [verificacion, setVerificacion] = useState({
    inProgress: false,
    message: '',
    success: null,
    faltantes: [] // [{num_empleado, nombre_empleado}]
  });

  // Cargar configuración desde localStorage
  useEffect(() => {
    if (isOpen) {
      const savedNombreDispositivo = localStorage.getItem('biometrico_nombre_dispositivo') || '';
      const savedIP = localStorage.getItem('biometrico_ip') || '192.168.1.201';
      const savedPort = localStorage.getItem('biometrico_port') || '4370';
      const savedTimeout = localStorage.getItem('biometrico_timeout') || '10000';
      const savedServidorDB = localStorage.getItem('biometrico_servidor_db') || '';
      const savedUsuarioDB = localStorage.getItem('biometrico_usuario_db') || '';
      const savedPasswordDB = localStorage.getItem('biometrico_password_db') || '';
      const savedBaseDatos = localStorage.getItem('biometrico_base_datos') || '';
      const savedTablaRegistros = localStorage.getItem('biometrico_tabla_registros') || 'registro';
      const savedTablaHuellas = localStorage.getItem('biometrico_tabla_huellas') || 'cat_biometricos_empleados';
      const savedRutaXampp = localStorage.getItem('biometrico_ruta_xampp') || 'C:\\xampp';

      setConfig({
        nombreDispositivo: savedNombreDispositivo,
        ip: savedIP,
        puerto: parseInt(savedPort),
        timeout: parseInt(savedTimeout),
        servidorDB: savedServidorDB,
        usuarioDB: savedUsuarioDB,
        passwordDB: savedPasswordDB,
        baseDatos: savedBaseDatos,
        tablaRegistros: savedTablaRegistros,
        tablaHuellas: savedTablaHuellas,
        rutaXampp: savedRutaXampp
      });

      // Verificar conexiones al abrir
      checkConnections();
    }
  }, [isOpen]);

  const checkConnections = async () => {
    // Verificar conexión al dispositivo
    try {
      if (window.biometrico) {
        const deviceStatus = await window.biometrico.testConnection();
        setStatus(prev => ({ ...prev, conectadoDispositivo: deviceStatus?.connected || false }));
      }
    } catch (error) {
      setStatus(prev => ({ ...prev, conectadoDispositivo: false }));
    }

    // Verificar conexión a la base de datos
    try {
      if (window.electronAPI?.checkDBConnection) {
        const dbStatus = await window.electronAPI.checkDBConnection();
        setStatus(prev => ({ ...prev, conectadoDB: dbStatus?.success || false }));
      }
    } catch (error) {
      setStatus(prev => ({ ...prev, conectadoDB: false }));
    }
  };

  const conectarDispositivo = async () => {
    setSyncStatus(prev => ({ ...prev, message: 'Conectando al dispositivo...', inProgress: true }));

    try {
      const response = await window.biometrico.connect({
        ip: config.ip,
        port: config.puerto,
        timeout: config.timeout
      });

      if (response?.success) {
        setStatus(prev => ({ ...prev, conectadoDispositivo: true }));
        setSyncStatus(prev => ({ ...prev, message: 'Dispositivo conectado', inProgress: false }));
        return true;
      } else {
        throw new Error(response?.message || 'Error al conectar');
      }
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        message: `Error: ${error.message}`,
        inProgress: false,
        success: false
      }));
      return false;
    }
  };

  // Poner en hora el dispositivo (sincroniza su reloj con el del PC)
  const actualizarHoraDispositivo = async () => {
    setTimeSyncStatus({ inProgress: true, message: 'Actualizando hora del dispositivo...', success: null });

    try {
      const response = await window.biometrico.setDeviceTime({
        ip: config.ip,
        port: config.puerto
      });

      if (response?.success) {
        setTimeSyncStatus({ inProgress: false, message: response.message, success: true });
      } else {
        throw new Error(response?.message || 'No se pudo actualizar la hora del dispositivo');
      }
    } catch (error) {
      setTimeSyncStatus({ inProgress: false, message: `Error: ${error.message}`, success: false });
    }
  };

  // Verificar que todos los empleados con huella en la BD ya estén registrados en el dispositivo
  const verificarHuellas = async () => {
    setVerificacion({ inProgress: true, message: 'Comparando huellas de la BD contra el dispositivo...', success: null, faltantes: [] });

    try {
      // 1. Empleados activos con huella en la BD
      const dbResponse = await window.electronAPI.getHuellasFromDB({
        servidor: config.servidorDB,
        usuario: config.usuarioDB,
        password: config.passwordDB,
        baseDatos: config.baseDatos,
        tabla: config.tablaHuellas,
        rutaXampp: config.rutaXampp
      });

      if (!dbResponse?.success) {
        throw new Error(dbResponse?.message || 'Error al obtener huellas de la BD');
      }

      const empleadosDB = new Map();
      (dbResponse.huellas || []).forEach(h => {
        if (!empleadosDB.has(h.num_empleado)) {
          empleadosDB.set(h.num_empleado, h.nombre_empleado);
        }
      });

      // 2. Conectar si hace falta y leer usuarios con huella del dispositivo
      if (!status.conectadoDispositivo) {
        const connected = await conectarDispositivo();
        if (!connected) return;
      }

      const deviceResponse = await window.biometrico.getAllUsersWithFingerprints({
        ip: config.ip,
        port: config.puerto
      });

      if (!deviceResponse?.success) {
        throw new Error(deviceResponse?.message || 'Error al obtener huellas del dispositivo');
      }

      const enrolladosDispositivo = new Set(
        (deviceResponse.users || [])
          .filter(u => (u.fingerprintCount || 0) > 0)
          .map(u => String(u.enrollNumber))
      );

      // 3. Comparar y armar la lista de faltantes
      const faltantes = [];
      empleadosDB.forEach((nombre, numEmpleado) => {
        if (!enrolladosDispositivo.has(String(numEmpleado))) {
          faltantes.push({ num_empleado: numEmpleado, nombre_empleado: nombre });
        }
      });

      setStats(prev => ({
        ...prev,
        huellasEnDB: empleadosDB.size,
        huellasEnDispositivo: empleadosDB.size - faltantes.length
      }));

      setVerificacion({
        inProgress: false,
        success: faltantes.length === 0,
        message: faltantes.length === 0
          ? `Confirmado: los ${empleadosDB.size} empleados con huella en la BD ya están en el dispositivo.`
          : `Faltan ${faltantes.length} de ${empleadosDB.size} empleados por subir al dispositivo.`,
        faltantes
      });
    } catch (error) {
      setVerificacion({ inProgress: false, message: `Error: ${error.message}`, success: false, faltantes: [] });
    }
  };

  // Sincronizar huellas de BD al dispositivo ZKTeco
  const sincronizarHuellas = async () => {
    setSyncStatus({
      type: 'huellas',
      inProgress: true,
      progress: 0,
      total: 0,
      message: 'Iniciando sincronización de huellas...',
      success: null,
      details: []
    });

    try {
      // 1. Conectar al dispositivo si no está conectado
      if (!status.conectadoDispositivo) {
        const connected = await conectarDispositivo();
        if (!connected) return;
      }

      // 2. Obtener huellas de la base de datos
      setSyncStatus(prev => ({ ...prev, message: 'Obteniendo huellas de la base de datos...' }));

      const dbResponse = await window.electronAPI.getHuellasFromDB({
        servidor: config.servidorDB,
        usuario: config.usuarioDB,
        password: config.passwordDB,
        baseDatos: config.baseDatos,
        tabla: config.tablaHuellas,
        rutaXampp: config.rutaXampp
      });

      if (!dbResponse?.success) {
        throw new Error(dbResponse?.message || 'Error al obtener huellas de la BD');
      }

      const huellas = dbResponse.huellas || [];
      setSyncStatus(prev => ({
        ...prev,
        total: huellas.length,
        message: `Encontradas ${huellas.length} huellas en la BD. Enviando al dispositivo...`
      }));

      // 3. Enviar cada huella al dispositivo
      let exitosas = 0;
      let errores = 0;
      const detalles = [];

      for (let i = 0; i < huellas.length; i++) {
        const huella = huellas[i];

        try {
          const result = await window.biometrico.sendCommand({
            action: 'set_fingerprint',
            data: {
              enrollNumber: huella.num_empleado.toString(),
              fingerIndex: huella.fingernumber || 0,
              templateData: huella.userfingerprint,
              userName: huella.nombre_empleado || null,
              typeUser: huella.typeuser || 1
            }
          });

          if (result?.success) {
            exitosas++;
            detalles.push({ empleado: huella.num_empleado, status: 'ok' });
          } else {
            errores++;
            detalles.push({ empleado: huella.num_empleado, status: 'error', mensaje: result?.message });
          }
        } catch (err) {
          errores++;
          detalles.push({ empleado: huella.num_empleado, status: 'error', mensaje: err.message });
        }

        setSyncStatus(prev => ({
          ...prev,
          progress: i + 1,
          message: `Procesando huella ${i + 1} de ${huellas.length}...`
        }));
      }

      setSyncStatus(prev => ({
        ...prev,
        inProgress: false,
        success: errores === 0,
        message: `Sincronización completada. ${exitosas} exitosas, ${errores} errores.`,
        details: detalles
      }));

    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        inProgress: false,
        success: false,
        message: `Error: ${error.message}`
      }));
    }
  };

  // Descargar checadas del dispositivo a la BD
  const descargarChecadas = async () => {
    setSyncStatus({
      type: 'checadas',
      inProgress: true,
      progress: 0,
      total: 0,
      message: 'Iniciando descarga de checadas...',
      success: null,
      details: []
    });

    try {
      // 1. Conectar al dispositivo si no está conectado
      if (!status.conectadoDispositivo) {
        const connected = await conectarDispositivo();
        if (!connected) return;
      }

      // 2. Obtener registros de asistencia del dispositivo
      setSyncStatus(prev => ({ ...prev, message: 'Obteniendo registros del dispositivo...' }));

      const attendanceResponse = await window.biometrico.getAttendance({
        ip: config.ip,
        port: config.puerto
      });

      if (!attendanceResponse?.success) {
        throw new Error(attendanceResponse?.message || 'Error al obtener registros del dispositivo');
      }

      // Los registros vienen procesados, necesitamos expandirlos
      const registrosProcesados = attendanceResponse.data || [];
      let totalRegistros = 0;
      const registrosParaInsertar = [];

      // Expandir los registros procesados a formato plano
      for (const usuario of registrosProcesados) {
        for (const [fecha, horarios] of Object.entries(usuario.days || {})) {
          // Agregar primera checada
          if (horarios.firstTime) {
            registrosParaInsertar.push({
              numEmp: usuario.userId,
              marcaje: `${fecha} ${horarios.firstTime}`,
              dispositivo: config.nombreDispositivo || config.ip,
              serial: ''
            });
            totalRegistros++;
          }
          // Agregar última checada si es diferente
          if (horarios.lastTime && horarios.lastTime !== horarios.firstTime) {
            registrosParaInsertar.push({
              numEmp: usuario.userId,
              marcaje: `${fecha} ${horarios.lastTime}`,
              dispositivo: config.nombreDispositivo || config.ip,
              serial: ''
            });
            totalRegistros++;
          }
        }
      }

      setSyncStatus(prev => ({
        ...prev,
        total: totalRegistros,
        message: `Encontrados ${totalRegistros} registros. Guardando en la base de datos...`
      }));

      // 3. Insertar registros en la base de datos (evitando duplicados)
      const removeProgresoListener = window.electronAPI.onProgresoChecadas(({ procesados, total }) => {
        setSyncStatus(prev => ({
          ...prev,
          progress: procesados,
          message: `Guardando registro ${procesados} de ${total} en la base de datos...`
        }));
      });

      let insertResponse;
      try {
        insertResponse = await window.electronAPI.insertChecadasToDB({
          servidor: config.servidorDB,
          usuario: config.usuarioDB,
          password: config.passwordDB,
          baseDatos: config.baseDatos,
          tabla: config.tablaRegistros,
          registros: registrosParaInsertar,
          rutaXampp: config.rutaXampp
        });
      } finally {
        removeProgresoListener();
      }

      if (!insertResponse?.success) {
        throw new Error(insertResponse?.message || 'Error al insertar registros');
      }

      // 4. Vaciar los registros de asistencia del dispositivo para liberar espacio
      let mensajeLimpieza = '';
      if (totalRegistros > 0) {
        setSyncStatus(prev => ({ ...prev, message: 'Vaciando registros del checador...' }));
        try {
          const clearResponse = await window.biometrico.clearAttendance({
            ip: config.ip,
            port: config.puerto
          });
          mensajeLimpieza = clearResponse?.success
            ? ' Checador vaciado.'
            : ` Aviso: no se pudo vaciar el checador (${clearResponse?.message || 'error desconocido'}).`;
        } catch (clearError) {
          mensajeLimpieza = ` Aviso: no se pudo vaciar el checador (${clearError.message}).`;
        }
      }

      setSyncStatus(prev => ({
        ...prev,
        inProgress: false,
        success: true,
        message: `Descarga completada. ${insertResponse.insertados || 0} nuevos registros, ${insertResponse.duplicados || 0} duplicados omitidos.${mensajeLimpieza}`,
        progress: totalRegistros
      }));

    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        inProgress: false,
        success: false,
        message: `Error: ${error.message}`
      }));
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="biometrico-modal-overlay" onClick={onClose}>
      <div className="biometrico-modal" onClick={e => e.stopPropagation()}>
        <div className="biometrico-modal-header">
          <div className="modal-title">
            <Fingerprint size={24} />
            <h2>Sincronización Biométrica</h2>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="biometrico-modal-body">
          {/* Estado de conexiones */}
          <div className="connection-status-section">
            <div className={`connection-item ${status.conectadoDispositivo ? 'connected' : 'disconnected'}`}>
              <Fingerprint size={18} />
              <span>{config.nombreDispositivo || 'Dispositivo ZKTeco'}</span>
              <span className="status-badge">
                {status.conectadoDispositivo ? 'Conectado' : 'Desconectado'}
              </span>
            </div>
            <div className={`connection-item ${config.servidorDB ? 'connected' : 'disconnected'}`}>
              <Database size={18} />
              <span>Base de Datos</span>
              <span className="status-badge">
                {config.servidorDB ? config.baseDatos : 'No configurado'}
              </span>
            </div>
          </div>

          {/* Configuración actual */}
          <div className="config-summary">
            <h4>Configuración Actual</h4>
            <div className="config-grid">
              <div className="config-item">
                <span className="config-label">Dispositivo:</span>
                <span className="config-value">{config.nombreDispositivo || 'Sin nombre'} ({config.ip}:{config.puerto})</span>
              </div>
              <div className="config-item">
                <span className="config-label">Servidor BD:</span>
                <span className="config-value">{config.servidorDB || 'No configurado'}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Tabla Huellas:</span>
                <span className="config-value">{config.tablaHuellas}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Tabla Registros:</span>
                <span className="config-value">{config.tablaRegistros}</span>
              </div>
            </div>
          </div>

          {/* Acciones de sincronización */}
          <div className="sync-actions">
            <button
              className="sync-btn sync-btn-upload"
              onClick={sincronizarHuellas}
              disabled={syncStatus.inProgress || !config.servidorDB}
            >
              <Upload size={20} />
              <div className="sync-btn-content">
                <span className="sync-btn-title">Sincronizar Huellas</span>
                <span className="sync-btn-desc">BD → Dispositivo ZKTeco</span>
              </div>
            </button>

            <button
              className="sync-btn sync-btn-download"
              onClick={descargarChecadas}
              disabled={syncStatus.inProgress || !config.servidorDB}
            >
              <Download size={20} />
              <div className="sync-btn-content">
                <span className="sync-btn-title">Descargar Checadas</span>
                <span className="sync-btn-desc">Dispositivo → Base de Datos</span>
              </div>
            </button>

            <button
              className="sync-btn sync-btn-time"
              onClick={actualizarHoraDispositivo}
              disabled={timeSyncStatus.inProgress || syncStatus.inProgress}
            >
              {timeSyncStatus.inProgress ? <Loader2 size={20} className="spinning" /> : <Clock size={20} />}
              <div className="sync-btn-content">
                <span className="sync-btn-title">Actualizar Hora del Dispositivo</span>
                <span className="sync-btn-desc">Pone en hora el reloj del ZKTeco con el del PC</span>
              </div>
            </button>
          </div>

          {/* Estado de la actualización de hora */}
          {timeSyncStatus.message && (
            <div className={`sync-status ${timeSyncStatus.success === true ? 'success' : timeSyncStatus.success === false ? 'error' : 'info'}`}>
              <div className="sync-status-header">
                {timeSyncStatus.inProgress ? (
                  <Loader2 size={20} className="spinning" />
                ) : timeSyncStatus.success ? (
                  <CheckCircle size={20} />
                ) : timeSyncStatus.success === false ? (
                  <AlertCircle size={20} />
                ) : (
                  <Clock size={20} />
                )}
                <span>{timeSyncStatus.message}</span>
              </div>
            </div>
          )}

          {/* Estado de sincronización */}
          {syncStatus.message && (
            <div className={`sync-status ${syncStatus.success === true ? 'success' : syncStatus.success === false ? 'error' : 'info'}`}>
              <div className="sync-status-header">
                {syncStatus.inProgress ? (
                  <Loader2 size={20} className="spinning" />
                ) : syncStatus.success ? (
                  <CheckCircle size={20} />
                ) : syncStatus.success === false ? (
                  <AlertCircle size={20} />
                ) : (
                  <RefreshCw size={20} />
                )}
                <span>{syncStatus.message}</span>
              </div>

              {syncStatus.inProgress && syncStatus.total > 0 && (
                <div className="sync-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${(syncStatus.progress / syncStatus.total) * 100}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {syncStatus.progress} / {syncStatus.total}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Ayuda */}
          <div className="sync-help">
            <h4>Instrucciones</h4>
            <ul>
              <li><strong>Sincronizar Huellas:</strong> Envía las huellas almacenadas en la base de datos al dispositivo ZKTeco.</li>
              <li><strong>Descargar Checadas:</strong> Obtiene los registros de asistencia del dispositivo, los guarda en la BD (sin duplicados) y luego vacía el checador para liberar espacio.</li>
              <li><strong>Actualizar Hora del Dispositivo:</strong> Sincroniza el reloj interno del ZKTeco con la hora actual del PC. Útil si las checadas llegan con hora incorrecta.</li>
              <li>Configure los parámetros de conexión en <strong>Configuración → Biométrico</strong>.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BiometricoSyncModal;
